/**
 * Transaction Service
 * 
 * Abstracts data fetching for transactions. Uses real API endpoints.
 * 
 * Follows Interface Segregation and Dependency Inversion principles.
 */

import type {
  TransactionResponse,
  TransactionListResponse,
  TransactionDetailResponse,
  TransactionQueryParams,
  CreateTransactionRequest,
  TransactionCreateResponse,
} from '@/app/types/transactions';
import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

/**
 * Transaction Service Interface
 * Define the contract that any implementation must follow
 */
export interface ITransactionService {
  queryTransactions(params: TransactionQueryParams): Promise<TransactionListResponse>;
  getTransactionById(id: string): Promise<TransactionDetailResponse | null>;
  getRelatedAdjustments(transactionId: string): Promise<TransactionResponse[]>;
  createTransaction(request: CreateTransactionRequest): Promise<TransactionCreateResponse>;
}

/**
 * Real API Implementation
 * Calls the admin transaction endpoints
 */
const apiTransactionService: ITransactionService = {
  async queryTransactions(params: TransactionQueryParams): Promise<TransactionListResponse> {
    try {
      // Build query params, filtering out undefined/null values
      const queryParams: Record<string, any> = {};
      if (params.community_ref_user_id !== undefined && params.community_ref_user_id !== null) {
        queryParams.community_ref_user_id = params.community_ref_user_id;
      }
      if (params.eligible_participant_id !== undefined && params.eligible_participant_id !== null) {
        queryParams.eligible_participant_id = params.eligible_participant_id;
      }
      if (params.transaction_id !== undefined && params.transaction_id !== null) {
        queryParams.transaction_id = params.transaction_id;
      }
      if (params.exploration_signup_id !== undefined && params.exploration_signup_id !== null) {
        queryParams.exploration_signup_id = params.exploration_signup_id;
      }
      if (params.partner_product_id !== undefined && params.partner_product_id !== null) {
        queryParams.partner_product_id = params.partner_product_id;
      }
      if (params.transaction_type !== undefined && params.transaction_type !== null) {
        queryParams.transaction_type = params.transaction_type;
      }
      if (params.source_system !== undefined && params.source_system !== null) {
        queryParams.source_system = params.source_system;
      }
      if (params.from_date !== undefined && params.from_date !== null) {
        queryParams.from_date = params.from_date;
      }
      if (params.to_date !== undefined && params.to_date !== null) {
        queryParams.to_date = params.to_date;
      }
      if (params.time_offset !== undefined && params.time_offset !== null) {
        queryParams.time_offset = params.time_offset;
      }
      // Always include limit and offset
      queryParams.limit = params.limit ?? 100;
      queryParams.offset = params.offset ?? 0;

      const response = await api.get<TransactionListResponse>(
        API_ENDPOINTS.admin.transactions,
        { params: queryParams }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to query transactions:', error);
      throw error;
    }
  },

  async getTransactionById(id: string): Promise<TransactionDetailResponse | null> {
    try {
      const response = await api.get<TransactionDetailResponse>(
        API_ENDPOINTS.admin.transactionDetail(id)
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get transaction:', error);
      throw error;
    }
  },

  async getRelatedAdjustments(transactionId: string): Promise<TransactionResponse[]> {
    try {
      // Query for transactions that reference this transaction_id in original_transaction_id
      const response = await api.get<TransactionListResponse>(API_ENDPOINTS.admin.transactions, {
        params: {
          transaction_id: transactionId,
          limit: 1000, // Get all adjustments
          offset: 0,
        },
      });
      // Filter to only return adjustments (not the original transaction)
      return response.data.transactions.filter(
        (tx) => tx.transaction_id !== transactionId
      );
    } catch (error: any) {
      console.error('Failed to get related adjustments:', error);
      throw error;
    }
  },


  async createTransaction(request: CreateTransactionRequest): Promise<TransactionCreateResponse> {
    try {
      const response = await api.post<TransactionCreateResponse>(
        API_ENDPOINTS.admin.transactions,
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to create transaction:', error);
      throw error;
    }
  },
};

// Export the active implementation
export const transactionService: ITransactionService = apiTransactionService;

// Also export as default for easier imports
export default transactionService;

